import React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui";

interface DataItem {
  label: string;
  value: number;
  color?: string;
}

interface AnalyticsBarChartProps {
  title: string;
  description?: string;
  data: DataItem[];
  className?: string;
  horizontal?: boolean;
  showValues?: boolean;
  showLabels?: boolean;
  height?: number;
  maxValue?: number;
  barSpacing?: number;
  valueFormatter?: (value: number) => string;
}

export const AnalyticsBarChart: React.FC<AnalyticsBarChartProps> = ({
  title,
  description,
  data,
  className = "",
  horizontal = false,
  showValues = true,
  showLabels = true,
  height = 300,
  maxValue,
  barSpacing = 8,
  valueFormatter = (value) => value.toLocaleString(),
}) => {
  // Find the maximum value if not provided
  const calculatedMaxValue =
    maxValue || Math.max(...data.map((item) => item.value)) * 1.1;

  // Calculate dimensions
  const chartPadding = {
    top: 20,
    right: 20,
    bottom: showLabels ? 40 : 20,
    left: 40,
  };
  const chartWidth = "100%";

  // Calculate bar dimensions based on data length
  const barCount = data.length;
  const chartInnerHeight = height - chartPadding.top - chartPadding.bottom;
  const chartInnerWidth = `calc(100% - ${
    chartPadding.left + chartPadding.right
  }px)`;

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium">{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <div
          className="relative"
          style={{
            height: `${height}px`,
            width: chartWidth,
          }}
        >
          {/* Y-axis labels (for vertical chart) or X-axis labels (for horizontal chart) */}
          {!horizontal && (
            <div
              className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-muted-foreground"
              style={{
                paddingTop: `${chartPadding.top}px`,
                paddingBottom: `${chartPadding.bottom}px`,
                height: "100%",
              }}
            >
              {[100, 75, 50, 25, 0].map((percent, index) => (
                <div key={index} className="flex items-center">
                  <span className="mr-1">
                    {((calculatedMaxValue * percent) / 100).toLocaleString()}
                  </span>
                  <div className="w-2 h-px bg-border" />
                </div>
              ))}
            </div>
          )}

          {/* Chart area */}
          <div
            className="absolute"
            style={{
              left: `${chartPadding.left}px`,
              top: `${chartPadding.top}px`,
              width: chartInnerWidth,
              height: `${chartInnerHeight}px`,
            }}
          >
            {/* Grid lines */}
            {!horizontal && (
              <div className="absolute inset-0">
                {[0, 25, 50, 75, 100].map((percent, index) => (
                  <div
                    key={index}
                    className="absolute w-full h-px bg-border"
                    style={{
                      bottom: `${(percent / 100) * chartInnerHeight}px`,
                    }}
                  />
                ))}
              </div>
            )}

            {/* Bars */}
            <div
              className={`relative h-full w-full flex ${
                horizontal ? "flex-col" : "items-end"
              }`}
              style={{ gap: `${barSpacing}px` }}
            >
              {data.map((item, index) => {
                const barWidth = horizontal
                  ? `${(item.value / calculatedMaxValue) * 100}%`
                  : `calc((100% - ${
                      (barCount - 1) * barSpacing
                    }px) / ${barCount})`;

                const barHeight = horizontal
                  ? `calc((100% - ${
                      (barCount - 1) * barSpacing
                    }px) / ${barCount})`
                  : `${(item.value / calculatedMaxValue) * 100}%`;

                return (
                  <div
                    key={index}
                    className="relative group"
                    style={{
                      width: horizontal ? "100%" : barWidth,
                      height: horizontal ? barHeight : "100%",
                    }}
                  >
                    {/* The actual bar */}
                    <div
                      className={`${
                        horizontal ? "h-full" : "w-full absolute bottom-0"
                      } rounded-sm transition-all duration-300 hover:opacity-80`}
                      style={{
                        width: horizontal ? barWidth : "100%",
                        height: horizontal ? "100%" : barHeight,
                        backgroundColor: item.color || `var(--primary)`,
                      }}
                    >
                      {/* Value labels */}
                      {showValues && (
                        <div
                          className={`absolute text-xs font-medium
                            ${
                              horizontal
                                ? "left-full ml-2 top-1/2 -translate-y-1/2"
                                : "bottom-full mb-1 left-1/2 -translate-x-1/2"
                            }`}
                        >
                          {valueFormatter(item.value)}
                        </div>
                      )}
                    </div>

                    {/* Bar labels */}
                    {showLabels && (
                      <div
                        className={`absolute text-xs text-muted-foreground truncate
                          ${
                            horizontal
                              ? "top-1/2 -translate-y-1/2 -left-[40px] w-[35px] text-right"
                              : "top-full mt-2 left-1/2 -translate-x-1/2"
                          }`}
                      >
                        {item.label}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
